import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Search, Edit2, Trash2, X, Loader2,
  Building2, Mail, Phone, User, Eye, EyeOff, Key, CheckCircle2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PortalClientsAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    company_name: '',
    contact_name: '',
    phone: '',
    password: ''
  });

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/clients`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      } else {
        toast.error('Kon klanten niet laden');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingClient) {
        // Update existing client
        const response = await fetch(`${API_URL}/api/portal/admin/clients/${editingClient.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            company_name: formData.company_name,
            contact_name: formData.contact_name,
            phone: formData.phone
          })
        });
        
        if (response.ok) {
          toast.success('Klant bijgewerkt');
          fetchClients();
          closeModal();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Kon klant niet bijwerken');
        }
      } else {
        // Create new client
        const response = await fetch(`${API_URL}/api/portal/admin/clients`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          toast.success('Klant aangemaakt');
          fetchClients();
          closeModal();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Kon klant niet aanmaken');
        }
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Weet je zeker dat je deze klant wilt verwijderen? Alle projecten worden ook verwijderd.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Klant verwijderd');
        fetchClients();
      } else {
        toast.error('Kon klant niet verwijderen');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const handleResetPassword = async (clientId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/clients/${clientId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Wachtwoord gereset');
        setShowPasswordReset(null);
        setNewPassword('');
      } else {
        toast.error('Kon wachtwoord niet resetten');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const toggleClientStatus = async (client) => {
    try {
      const response = await fetch(`${API_URL}/api/portal/admin/clients/${client.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ is_active: !client.is_active })
      });
      
      if (response.ok) {
        toast.success(client.is_active ? 'Klant gedeactiveerd' : 'Klant geactiveerd');
        fetchClients();
      } else {
        toast.error('Kon status niet wijzigen');
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    }
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setFormData({
      email: client.email,
      company_name: client.company_name,
      contact_name: client.contact_name,
      phone: client.phone || '',
      password: ''
    });
    setShowModal(true);
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormData({
      email: '',
      company_name: '',
      contact_name: '',
      phone: '',
      password: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setFormData({
      email: '',
      company_name: '',
      contact_name: '',
      phone: '',
      password: ''
    });
  };

  const filteredClients = clients.filter(client =>
    client.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.contact_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Nooit';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Klantenbeheer</h1>
            <p className="text-slate-600">Beheer klanten voor het klantportaal</p>
          </div>
          
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            data-testid="add-client-btn"
          >
            <Plus className="w-5 h-5" />
            Nieuwe Klant
          </button>
        </div>

        {/* Search */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Zoek op bedrijf, naam of email..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="client-search"
            />
          </div>
        </div>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchQuery ? 'Geen klanten gevonden' : 'Nog geen klanten'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white border rounded-xl p-6 ${
                  !client.is_active ? 'border-red-200 bg-red-50/50' : 'border-slate-200'
                }`}
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      client.is_active ? 'bg-blue-100' : 'bg-red-100'
                    }`}>
                      <Building2 className={`w-6 h-6 ${
                        client.is_active ? 'text-blue-600' : 'text-red-600'
                      }`} />
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{client.company_name}</h3>
                        {!client.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                            Inactief
                          </span>
                        )}
                      </div>
                      <p className="text-slate-500 text-sm">{client.contact_name}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {client.email}
                        </span>
                        {client.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {client.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-4 hidden sm:block">
                      <p className="text-xs text-slate-400">Laatste login</p>
                      <p className="text-sm text-slate-600">{formatDate(client.last_login)}</p>
                    </div>
                    
                    <button
                      onClick={() => setShowPasswordReset(client.id)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Wachtwoord resetten"
                    >
                      <Key className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => toggleClientStatus(client)}
                      className={`p-2 rounded-lg transition-all ${
                        client.is_active 
                          ? 'text-slate-400 hover:text-yellow-600 hover:bg-yellow-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={client.is_active ? 'Deactiveren' : 'Activeren'}
                    >
                      <CheckCircle2 className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => openEditModal(client)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Bewerken"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Verwijderen"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Password Reset Form */}
                <AnimatePresence>
                  {showPasswordReset === client.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-slate-200"
                    >
                      <div className="flex items-center gap-4">
                        <div className="relative flex-1 max-w-xs">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Nieuw wachtwoord"
                            className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <button
                          onClick={() => handleResetPassword(client.id)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          Opslaan
                        </button>
                        <button
                          onClick={() => {
                            setShowPasswordReset(null);
                            setNewPassword('');
                          }}
                          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          Annuleren
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingClient ? 'Klant Bewerken' : 'Nieuwe Klant'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      disabled={editingClient}
                      data-testid="client-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Bedrijfsnaam *
                    </label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="client-company-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Contactpersoon *
                    </label>
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="client-contact-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Telefoon
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="client-phone-input"
                    />
                  </div>

                  {!editingClient && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Wachtwoord *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full px-4 py-2 pr-10 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required={!editingClient}
                          minLength={6}
                          data-testid="client-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      data-testid="client-submit-btn"
                    >
                      {editingClient ? 'Opslaan' : 'Aanmaken'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PortalClientsAdmin;
